import React from 'react';
import { Card, Spin } from 'antd';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { formatDateTime } from '../../../../utils/functions';
import CommonTable from '../../../global/table';

const EnhancedAutoresponseSentDetails = ({ id }) => {

    // Fetch details
    const { data: detailData, isLoading } = useGenericQuery({
        endpoint: `reports/autoresponder/sent/list?id=${id}`,
        queryKey: ["autoresponse-sent-detail", id],
        enabled: !!id
    });

    // Debugging: Log the incoming data to help verify structure
    console.log("EnhancedAutoresponseSentDetails detailData:", detailData);

    // Handle various data structures
    let result = null;
    if (Array.isArray(detailData)) {
        result = detailData[0];
    } else if (Array.isArray(detailData?.results)) {
        result = detailData.results[0];
    } else if (Array.isArray(detailData?.data?.results)) {
        result = detailData.data.results[0];
    } else {
        result = detailData?.results || detailData; // final fallback
    }

    // Extract main parts with case-insensitive fallback
    const autoresponder = result?.Autoresponder?.[0] || result?.autoresponder?.[0] || {};
    // Try 'leads' first, then 'leadSent'. Ensure it's an array.
    const leads = result?.leads || result?.leadSent || [];
    console.log("leads", leads)


    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" tip="Loading details..." />
            </div>
        );
    }

    if (!result) {
        return <div className="text-center p-6 text-gray-500">No details found.</div>;
    }


    // Map leads for the table
    const leadsData = leads.map((item, index) => {
        // Old code uses item?.leadDetail for details.
        // If item itself has the details (fallback case), we try that too.
        const detail = item.leadDetail || item;

        return {
            key: item.id || item._id || index,
            sNo: index + 1,
            sentAt: item.sentAt || "-",
            // Check detail for fields, fallback to email if name is empty
            name: [detail.firstName, detail.lastName].filter(Boolean).join(" ") || detail.email || "-",
            email: detail.email || "-",
            phone: detail.phone || "-",
            leadSource: detail.leadSource || "-"
        };
    });

    const columns = [
        {
            title: "S#",
            dataIndex: "sNo",
            key: "sNo",
            width: "50px",
        },
        {
            title: "Sent Time",
            dataIndex: "sentAt",
            key: "sentAt",
            render: (text) => formatDateTime(text, "h:mm") || "-"
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Lead Source",
            dataIndex: "leadSource",
            key: "leadSource",
            render: (text) => <span className="capitalize">{text}</span>
        }
    ];

    return (
        <div className="p-4 bg-gray-50 min-h-full">
            <Card className="shadow-sm mb-6" bordered={false}>
                <h3 className="text-base font-semibold text-gray-700 mb-4 border-b pb-2">Message</h3>

                <div className="rounded border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm text-left">
                        <tbody>
                            <tr className="border-b border-gray-100">
                                <td className="px-4 py-3 bg-gray-50 font-semibold w-1/4 text-gray-600">Sender Name</td>
                                <td className="px-4 py-3 text-gray-800">{autoresponder.senderName || "-"}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                                <td className="px-4 py-3 bg-gray-50 font-semibold text-gray-600">Sender Email</td>
                                <td className="px-4 py-3 text-gray-800">{autoresponder.senderEmail || "-"}</td>
                            </tr>
                            <tr className="border-b border-gray-100">
                                <td className="px-4 py-3 bg-gray-50 font-semibold text-gray-600">Autoresponder Name</td>
                                {/* Using title for Autoresponder Name as it typically holds the template name */}
                                <td className="px-4 py-3 text-gray-800">{autoresponder.name || autoresponder.title || "-"}</td>
                            </tr>
                            <tr>
                                <td className="px-4 py-3 bg-gray-50 font-semibold text-gray-600 align-top">Autoresponder Message</td>
                                <td className="px-4 py-3 text-gray-800">
                                    <div
                                        className="prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{ __html: autoresponder.message }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="mt-6 flex items-center gap-4 text-sm">
                    <span className="font-bold text-gray-700">Send Date</span>
                    <span className="text-gray-600">
                        {formatDateTime(result.dateAsPerUserTimezone || result.processDate)}
                    </span>
                </div>
            </Card>

            <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-base font-semibold text-gray-700 mb-4 px-2">Autoresponder Sent Lead(s)</h3>
                <CommonTable
                    columns={columns}
                    data={leadsData}
                    pagination={false}
                />
            </div>
        </div>
    );
};

export default EnhancedAutoresponseSentDetails;
